import React, { useState, useEffect } from 'react';
import { UserCircleIcon, PaperAirplaneIcon } from '@heroicons/react/24/solid';

interface Comment {
  id: number;
  user_name: string;
  comment_text: string;
  created_at: string;
  user_role?: string;
  avatar?: string;
  replies: Comment[];
}

interface CommentSectionProps {
  courseContentId: number;
  /** Highlight & scroll tới bình luận (từ tab Bình luận khóa học) */
  highlightCommentId?: number | null;
}

const CommentSection: React.FC<CommentSectionProps> = ({ courseContentId, highlightCommentId }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState<number | null>(null);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/comments/${courseContentId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
      });
      const data = await res.json();
      if (data.success) {
        setComments(data.data);
      }
    } catch (error) {
      console.error('Error fetching comments', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (courseContentId) {
      fetchComments();
      setReplyingTo(null);
      setNewComment('');
      setReplyText('');
    }
  }, [courseContentId]);

  useEffect(() => {
    if (!highlightCommentId || loading || comments.length === 0) return;
    const el = document.getElementById(`comment-${highlightCommentId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setReplyingTo(highlightCommentId);
    }
  }, [highlightCommentId, loading, comments]);

  const handleSubmit = async (e: React.FormEvent, parentId: number | null = null) => {
    e.preventDefault();
    const text = parentId ? replyText : newComment;
    if (!text.trim()) return;

    try {
      const res = await fetch('/api/v1/comments', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          courseContentId,
          commentText: text,
          parentId
        })
      });
      const data = await res.json();
      if (data.success) {
        if (parentId) {
          setReplyText('');
          setReplyingTo(null);
        } else {
          setNewComment('');
        }
        fetchComments(); // Refresh list to get proper structure
      }
    } catch (error) {
      console.error('Error posting comment', error);
    }
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return new Intl.DateTimeFormat('vi-VN', { dateStyle: 'medium', timeStyle: 'short' }).format(d);
  };

  const renderComment = (comment: Comment, isReply = false) => {
    const isTeacher = comment.user_role === 'ADMIN' || comment.user_role === 'TEACHER';
    const isHighlighted = highlightCommentId === comment.id;
    
    return (
      <div
        id={`comment-${comment.id}`}
        key={comment.id}
        className={`flex space-x-3 ${isReply ? 'mt-4' : 'mb-6'} ${isHighlighted ? 'rounded-2xl ring-2 ring-m2-primary ring-offset-2' : ''}`}
      >
        <div className="flex-shrink-0">
          {comment.avatar ? (
            <img src={comment.avatar} alt={comment.user_name} className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <UserCircleIcon className={`w-10 h-10 ${isTeacher ? 'text-m2-primary' : 'text-m2-outline'}`} />
          )}
        </div>
        <div className="flex-grow">
          <div className={`bg-m2-surface-low rounded-2xl p-4 border ${isHighlighted ? 'border-m2-primary bg-m2-primary/5' : 'border-m2-outline-variant'}`}>
            <div className="flex items-center space-x-2 mb-1">
              <span className="font-semibold text-m2-on-surface text-sm">{comment.user_name}</span>
              {isTeacher && <span className="bg-m2-primary/15 text-m2-primary text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide">Giáo viên</span>}
              <span className="text-xs text-m2-on-surface-variant">• {formatDate(comment.created_at)}</span>
            </div>
            <p className="text-m2-on-surface whitespace-pre-wrap text-sm">{comment.comment_text}</p>
          </div>
          
          {!isReply && (
            <div className="mt-1 ml-2">
              <button 
                onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                className="text-xs font-medium text-m2-on-surface-variant hover:text-m2-primary transition-colors"
              >
                Phản hồi
              </button>
            </div>
          )}

          {/* Reply Input */}
          {replyingTo === comment.id && !isReply && (
            <div className="mt-3 flex space-x-3 items-start">
              <UserCircleIcon className="w-8 h-8 text-m2-outline flex-shrink-0 mt-1" />
              <form onSubmit={(e) => handleSubmit(e, comment.id)} className="flex-grow flex items-center relative">
                <input 
                  type="text" 
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Viết phản hồi..."
                  className="w-full bg-m2-surface-container border border-m2-outline rounded-full py-2 pl-4 pr-12 focus:outline-none focus:ring-2 focus:ring-m2-primary focus:border-transparent text-sm"
                  autoFocus
                />
                <button 
                  type="submit"
                  disabled={!replyText.trim()}
                  className="absolute right-2 p-1.5 bg-m2-primary text-m2-on-primary rounded-full hover:bg-m2-primary-container disabled:opacity-50 transition-colors"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                </button>
              </form>
            </div>
          )}

          {/* Nested Replies */}
          {comment.replies && comment.replies.length > 0 && (
            <div className="mt-2">
              {comment.replies.map(reply => renderComment(reply, true))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!courseContentId) {
    return <div className="text-m2-on-surface-variant text-center py-8">Vui lòng chọn bài học để xem bình luận.</div>;
  }

  return (
    <div className="bg-m2-surface-container rounded-xl">
      {/* Root Comment Input */}
      <div className="flex space-x-4 mb-8">
        <UserCircleIcon className="w-12 h-12 text-m2-outline-variant flex-shrink-0" />
        <form onSubmit={(e) => handleSubmit(e)} className="flex-grow flex items-start flex-col relative">
          <textarea 
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Bạn có câu hỏi gì về bài học này?"
            className="w-full bg-m2-surface-low border border-m2-outline-variant rounded-xl p-4 pr-16 focus:outline-none focus:ring-2 focus:ring-m2-primary focus:border-transparent text-sm min-h-[80px] resize-none"
          />
          <button 
            type="submit"
            disabled={!newComment.trim()}
            className="absolute bottom-3 right-3 px-4 py-1.5 bg-m2-primary text-m2-on-primary rounded-lg hover:bg-m2-primary-container disabled:opacity-50 transition-colors text-sm font-medium flex items-center"
          >
            Gửi
          </button>
        </form>
      </div>

      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-8 text-m2-on-surface-variant"><i className="fas fa-spinner fa-spin mr-2"></i> Đang tải bình luận...</div>
        ) : comments.length === 0 ? (
          <div className="text-center py-12 bg-m2-surface-low rounded-xl border border-dashed border-m2-outline-variant">
            <p className="text-m2-on-surface-variant">Chưa có bình luận nào. Hãy là người đầu tiên đặt câu hỏi!</p>
          </div>
        ) : (
          comments.map(c => renderComment(c))
        )}
      </div>
    </div>
  );
};

export default CommentSection;
